import React, { useEffect, useState, useCallback, useRef } from "react";
import { Tour, Grid, notification } from "antd";
import type { TourProps } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useGuide } from "../context/GuideContext";
import { getTourDefinition, getTourSteps } from "../config/tourDefinitions";
import type { GuideId } from "../types";

const { useBreakpoint } = Grid;

/** Max time to wait for elements: INITIAL_DELAY + MAX_RETRIES * RETRY_INTERVAL (~8s) */
const MAX_RETRIES = 25;
const RETRY_INTERVAL_MS = 300;
const INITIAL_DELAY_MS = 300;

/**
 * TourManager - Central component that manages and renders all tours.
 * Automatically navigates to the correct route and polls for DOM elements
 * (supports lazy-loaded pages with up to ~8s wait time).
 *
 * IMPORTANT: Always renders <Tour> and controls visibility via `open` prop
 * instead of conditional rendering. This ensures Ant Design properly cleans
 * up the mask overlay when the tour closes (prevents stuck overlays).
 */
export const TourManager: React.FC = () => {
  const { t } = useTranslation("guide");
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTour, endTour, markGuideComplete } = useGuide();

  const [currentStep, setCurrentStep] = useState(0);
  const [tourSteps, setTourSteps] = useState<TourProps["steps"]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const isMobile = !screens.md;
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref to always read latest activeTour inside async polling callbacks
  const activeTourRef = useRef<GuideId | null>(null);

  useEffect(() => {
    activeTourRef.current = activeTour;
  }, [activeTour]);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => clearRetryTimer, [clearRetryTimer]);

  const buildTourSteps = useCallback(
    (tourId: GuideId) => {
      const stepConfigs = getTourSteps(tourId);

      const steps: TourProps["steps"] = stepConfigs.map((step) => {
        const targetElement = document.querySelector(
          step.target,
        ) as HTMLElement | null;

        return {
          title: t(step.titleKey),
          description: t(step.descriptionKey),
          target: targetElement || undefined,
          placement: isMobile ? "center" : step.placement,
          mask: {
            style: {
              boxShadow: "inset 0 0 15px #333",
            },
          },
        };
      });

      const hasValidTargets = steps.some((step) => step.target);
      if (!hasValidTargets) {
        notification.warning({
          message: t(
            "tours.common.elementsNotFound",
            "Guide elements not found on this page",
          ),
        });
        endTour();
        return;
      }

      setTourSteps(steps);
      setCurrentStep(0);
      setIsOpen(true);
    },
    [t, isMobile, endTour],
  );

  // Keep a ref to always call the latest buildTourSteps from async callbacks
  const buildTourStepsRef = useRef(buildTourSteps);
  useEffect(() => {
    buildTourStepsRef.current = buildTourSteps;
  }, [buildTourSteps]);

  /**
   * Poll for target elements to appear in the DOM, then build tour steps.
   * Uses refs to avoid stale closures in setTimeout callbacks.
   */
  const pollForElements = useCallback(
    (tourId: GuideId) => {
      let retries = 0;

      const tryBuild = () => {
        // Abort if tour was cancelled or changed while waiting
        if (activeTourRef.current !== tourId) return;

        const stepConfigs = getTourSteps(tourId);
        const hasElements = stepConfigs.some((step) =>
          document.querySelector(step.target),
        );

        if (hasElements) {
          buildTourStepsRef.current(tourId);
        } else if (retries < MAX_RETRIES) {
          retries++;
          retryTimerRef.current = setTimeout(tryBuild, RETRY_INTERVAL_MS);
        } else {
          // Max retries reached - buildTourSteps will show the error
          buildTourStepsRef.current(tourId);
        }
      };

      retryTimerRef.current = setTimeout(tryBuild, INITIAL_DELAY_MS);
    },
    [], // No deps needed - uses refs for all mutable values
  );

  // Main effect: navigate if needed, then poll for elements
  useEffect(() => {
    clearRetryTimer();

    if (!activeTour) {
      setIsOpen(false);
      setTourSteps([]);
      setCurrentStep(0);
      return;
    }

    const definition = getTourDefinition(activeTour);
    if (!definition) {
      console.warn(`Tour definition not found for: ${activeTour}`);
      endTour();
      return;
    }

    // Navigate to the tour's route if we're not already there
    if (definition.route && !location.pathname.startsWith(definition.route)) {
      navigate(definition.route);
      // Effect re-triggers when location.pathname changes after navigation
      return;
    }

    // On the correct route - poll for elements then start tour
    pollForElements(activeTour);
  }, [
    activeTour,
    location.pathname,
    clearRetryTimer,
    endTour,
    navigate,
    pollForElements,
  ]);

  const handleClose = () => {
    clearRetryTimer();
    setIsOpen(false);
    endTour();
    setCurrentStep(0);
  };

  const handleFinish = () => {
    if (activeTour) {
      markGuideComplete(activeTour);
      notification.success({
        message: t("tours.common.completed", "Guide completed!"),
      });
    }
    setIsOpen(false);
    setCurrentStep(0);
  };

  return (
    <Tour
      open={isOpen}
      steps={tourSteps}
      current={currentStep}
      onChange={setCurrentStep}
      onClose={handleClose}
      onFinish={handleFinish}
      indicatorsRender={(current, total) => (
        <span>
          {current + 1} / {total}
        </span>
      )}
    />
  );
};
